// @noflow
import React from "react";
import { Modal, Table } from "react-bootstrap";
import { translate } from "../../../components/IntlProviderWrapper";
import { niceNumber } from "../../../lib/Util";
import type { IPreplanningDataExpectedProduction } from "../../../lib/backend/manufacturing2.generated.types";
import type { ProductionUntilTodayOverall } from "../../../lib/Models";

import "./PreplanningProductGroupSumModal.scss";

type Props = {
    show: boolean;
    onHide: () => void;
    product_group_sum_map: Map<string, ProductionUntilTodayOverall>;
    expected_productions: IPreplanningDataExpectedProduction[];
    date: Date;
};

const PreplanningProductGroupSumModal = (props: Props) => {
    if (!props.product_group_sum_map) {
        return null;
    }

    let content: React.ReactNode[] = [];

    // Until today does not include today's shifts, while overall includes all shifts

    let total_planned_overall = 0;
    let total_sales_plan = 0;

    for (const [key, value] of props.product_group_sum_map) {
        const { planned } = value;
        const planned_overall = planned.overall;

        total_planned_overall += planned_overall;

        const material_group = props.expected_productions.find(
            expected_production => expected_production.material_group === key
        );
        let sales_plan_qty = 0;
        if (material_group !== undefined) {
            sales_plan_qty = material_group.quantity;
        }
        const planned_over_sales = planned_overall - sales_plan_qty;
        total_sales_plan += sales_plan_qty;
        let status_plan_planned = "";
        if (sales_plan_qty > 0) {
            status_plan_planned = niceNumber(Math.round((planned_over_sales / sales_plan_qty) * 100));
        } else if (planned_overall === 0) {
            status_plan_planned = "0";
        } else {
            status_plan_planned = "\u221E";
        }

        content.push(
            <tr key={key}>
                <td style={{ textAlign: "start" }}>{key}</td>
                <td>{niceNumber(sales_plan_qty)}</td>
                <td>{niceNumber(planned_overall)}</td>
                <td>{niceNumber(planned_over_sales)}</td>
                <td>{status_plan_planned}%</td>
            </tr>
        );
    }

    let total_status_plan_planned = "";
    if (total_sales_plan > 0) {
        total_status_plan_planned = niceNumber(
            Math.round(((total_planned_overall - total_sales_plan) / total_sales_plan) * 100)
        );
    } else if (total_planned_overall === 0) {
        total_status_plan_planned = "0";
    } else {
        total_status_plan_planned = "\u221E";
    }

    return (
        <Modal show={props.show} onHide={props.onHide} size="lg">
            <Modal.Header>
                <Modal.Title>
                    {translate("Preplanning.production_status", "Production status")}:{" "}
                    <strong>
                        {translate(`common.month${props.date.getMonth()}`)} {props.date.getFullYear()}
                    </strong>
                </Modal.Title>
                <button type="button" className="close" onClick={props.onHide}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="table-responsive">
                    <Table className="product-group-sum-table react-table table-bordered">
                        <thead>
                            <tr>
                                <th>{translate("Preplanning.product_group", "Product group")}</th>
                                <th>
                                    {translate("Production.expected", "Expected")}
                                    <span
                                        className="info-icon"
                                        title={translate(
                                            "Production.expected_monthly_production",
                                            "Expected monthly production"
                                        )}
                                    >
                                        <i className="fas fa-info-circle ml-1"></i>
                                    </span>
                                </th>
                                <th>
                                    {translate("Preplanning.planned", "Planned")}
                                    <span
                                        className="info-icon"
                                        title={translate(
                                            "Preplanning.planned_until_end_of_month",
                                            "Produced until today and planned for the rest of the month"
                                        )}
                                    >
                                        <i className="fas fa-info-circle ml-1"></i>
                                    </span>
                                </th>
                                <th>
                                    {translate("common.difference", "Difference")}
                                    <span
                                        className="info-icon"
                                        title={translate(
                                            "Preplanning.difference_planned_expected",
                                            "Difference (planned - expected)"
                                        )}
                                    >
                                        <i className="fas fa-info-circle ml-1"></i>
                                    </span>
                                </th>
                                <th>
                                    {translate("Preplanning.relative_difference", "Relative difference")}
                                    <span
                                        className="info-icon"
                                        title={translate(
                                            "Preplanning.difference_planned_expected_relative",
                                            "Relative difference (planned - expected) / expected"
                                        )}
                                    >
                                        <i className="fas fa-info-circle ml-1"></i>
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {content}
                            <tr>
                                <td>
                                    <strong>{translate("Preplanning.production_status", "Production status")}</strong>
                                </td>
                                <td>
                                    <strong>{niceNumber(total_sales_plan)}</strong>
                                </td>
                                <td>
                                    <strong>{niceNumber(total_planned_overall)}</strong>
                                </td>
                                <td>
                                    <strong>{niceNumber(total_planned_overall - total_sales_plan)}</strong>
                                </td>
                                <td>
                                    <strong>{total_status_plan_planned + "%"}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PreplanningProductGroupSumModal;
